import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { 
  Trophy, Plus, Edit2, Trash2, Users, CheckCircle, Clock, 
  Target, Award, TrendingUp, BookOpen, MessageSquare, 
  FileText, Flame, FolderOpen, Heart, Star, Calendar
} from 'lucide-react';
import { toast } from 'sonner';


import { BACKEND_URL } from '../../config/api';
const API = BACKEND_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

const CHALLENGE_TYPES = [
  { value: 'profile', label: 'Profile Completion', icon: Users, color: 'bg-blue-500' },
  { value: 'course', label: 'Course Progress', icon: BookOpen, color: 'bg-green-500' },
  { value: 'quiz', label: 'Quiz Performance', icon: Target, color: 'bg-purple-500' },
  { value: 'community', label: 'Community Engagement', icon: MessageSquare, color: 'bg-orange-500' },
  { value: 'assignment', label: 'Assignment Submission', icon: FileText, color: 'bg-red-500' },
  { value: 'streak', label: 'Daily Streak', icon: Flame, color: 'bg-yellow-500' },
  { value: 'project', label: 'Project Submission', icon: FolderOpen, color: 'bg-indigo-500' },
  { value: 'behaviour', label: 'Positive Behaviour', icon: Heart, color: 'bg-pink-500' },
  { value: 'special', label: 'Special Achievement', icon: Star, color: 'bg-cyan-500' }
];

export default function ChallengeManagement({ user, onLogout }) {
  const [challenges, setChallenges] = useState([]);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [selectedChallengeCompletions, setSelectedChallengeCompletions] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    challenge_type: '',
    deadline: '',
    gp_reward: 10,
    requirements: {},
    target_courses: [],
    target_students: [],
    auto_assign_new_students: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [challengesRes, coursesRes, studentsRes] = await Promise.all([
        axios.get(`${API}/api/challenges`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/courses`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/users`, { headers: getAuthHeaders() })
      ]);

      setChallenges(challengesRes.data);
      setCourses(coursesRes.data);
      setStudents(studentsRes.data.filter(u => u.role === 'student'));
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load challenge data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRequirementChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        [field]: parseInt(value) || 0
      }
    }));
  };

  const handleChallengeTypeChange = (type) => {
    // Set default requirements based on challenge type
    let defaultRequirements = {};
    
    switch(type) {
      case 'streak':
        defaultRequirements = { streak_days: 7 };
        break;
      case 'course':
        defaultRequirements = { progress_percentage: 100 };
        break;
      case 'quiz':
        defaultRequirements = { quiz_count: 3, min_score: 80 };
        break;
      case 'assignment':
        defaultRequirements = { assignment_count: 3 };
        break;
      case 'community':
        defaultRequirements = { post_count: 5 };
        break;
      case 'profile':
        defaultRequirements = {}; // Auto-checks profile_verified
        break;
      default:
        defaultRequirements = {};
    }
    
    setFormData(prev => ({
      ...prev,
      challenge_type: type,
      requirements: defaultRequirements
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      challenge_type: '',
      deadline: '',
      gp_reward: 10,
      requirements: {},
      target_courses: [],
      target_students: [],
      auto_assign_new_students: false
    });
    setEditingChallenge(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.challenge_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const challengeData = {
        ...formData,
        gp_reward: parseInt(formData.gp_reward),
        deadline: formData.deadline || null
      };

      if (editingChallenge) {
        await axios.put(`${API}/api/challenges/${editingChallenge.id}`, challengeData, { headers: getAuthHeaders() });
        toast.success('Challenge updated successfully!');
      } else {
        await axios.post(`${API}/api/challenges`, challengeData, { headers: getAuthHeaders() });
        toast.success('Challenge created successfully!');
      }

      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save challenge:', error);
      toast.error(error.response?.data?.detail || 'Failed to save challenge');
    }
  };

  const handleEdit = (challenge) => {
    setFormData({
      title: challenge.title,
      description: challenge.description,
      challenge_type: challenge.challenge_type,
      deadline: challenge.deadline || '',
      gp_reward: challenge.gp_reward,
      requirements: challenge.requirements || {},
      target_courses: challenge.target_courses || [],
      target_students: challenge.target_students || [],
      auto_assign_new_students: challenge.auto_assign_new_students || false
    });
    setEditingChallenge(challenge);
    setShowCreateModal(true);
  };

  const handleDelete = async (challengeId) => {
    if (!window.confirm('Are you sure you want to delete this challenge? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/challenges/${challengeId}`, { headers: getAuthHeaders() });
      toast.success('Challenge deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete challenge:', error);
      toast.error('Failed to delete challenge');
    }
  };

  const toggleChallengeStatus = async (challengeId, isActive) => {
    try {
      await axios.put(`${API}/api/challenges/${challengeId}`, { is_active: !isActive }, { headers: getAuthHeaders() });
      toast.success(`Challenge ${!isActive ? 'activated' : 'deactivated'} successfully`);
      fetchData();
    } catch (error) {
      console.error('Failed to toggle challenge status:', error);
      toast.error('Failed to update challenge status');
    }
  };

  const viewCompletions = async (challenge) => {
    try {
      const res = await axios.get(`${API}/api/challenges/${challenge.id}/completions`, { headers: getAuthHeaders() });
      setSelectedChallengeCompletions({ challenge, completions: res.data });
    } catch (error) {
      console.error('Failed to fetch completions:', error);
      toast.error('Failed to load challenge completions');
    }
  };

  const getChallengeTypeInfo = (type) => {
    return CHALLENGE_TYPES.find(t => t.value === type) || CHALLENGE_TYPES[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No deadline';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout}><div className="p-8">Loading...</div></Layout>;
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-navy flex items-center space-x-3">
              <Trophy className="w-8 h-8 text-yellow-500" strokeWidth={2} />
              <span>Challenge Management</span>
            </h1>
            <p className="text-[#4D4D4D] mt-2 font-light">Create and manage gamified challenges for students</p>
          </div>

          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => {
                  resetForm();
                  setShowCreateModal(true);
                }}
                className="bg-[#4CAF50] hover:bg-[#419544] text-white font-semibold px-6 py-3 rounded-xl transition-smooth flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" strokeWidth={2} />
                <span>Create Challenge</span>
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-2xl max-h-[90vh] bg-white rounded-2xl border-0 shadow-2xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-navy">
                  {editingChallenge ? 'Edit Challenge' : 'Create New Challenge'}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-6 pb-4">
                {/* Challenge Title */}
                <div>
                  <Label htmlFor="title" className="text-sm font-semibold">Challenge Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Complete your profile to 100%"
                    required
                    className="mt-1"
                  />
                </div>

                {/* Challenge Description */}
                <div>
                  <Label htmlFor="description" className="text-sm font-semibold">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Fill out all sections of your profile to unlock your learning potential!"
                    required
                    className="mt-1"
                    rows={3}
                  />
                </div>

                {/* Challenge Type and GP Reward */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="challenge_type" className="text-sm font-semibold">Challenge Type *</Label>
                    <Select value={formData.challenge_type} onValueChange={handleChallengeTypeChange}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select type..." />
                      </SelectTrigger>
                      <SelectContent>
                        {CHALLENGE_TYPES.map((type) => {
                          const IconComponent = type.icon;
                          return (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center space-x-2">
                                <div className={`w-4 h-4 rounded-full ${type.color}`}></div>
                                <span>{type.label}</span>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="gp_reward" className="text-sm font-semibold">GP Reward *</Label>
                    <Input
                      id="gp_reward"
                      type="number"
                      min="1"
                      value={formData.gp_reward}
                      onChange={(e) => handleInputChange('gp_reward', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Dynamic Requirement Fields */}
                {formData.challenge_type && (
                  <div className="p-4 bg-[#F7F7F7] rounded-xl border-2 border-[#E7E7E7]">
                    <h3 className="text-sm font-bold text-navy mb-3">Challenge Requirements</h3>
                    
                    {/* Streak Challenge */}
                    {formData.challenge_type === 'streak' && (
                      <div>
                        <Label htmlFor="streak_days" className="text-sm font-semibold">Number of Days *</Label>
                        <Input
                          id="streak_days"
                          type="number"
                          min="1"
                          value={formData.requirements.streak_days || 7}
                          onChange={(e) => handleRequirementChange('streak_days', e.target.value)}
                          placeholder="7"
                          className="mt-1"
                        />
                        <p className="text-xs text-[#4D4D4D] mt-1">Student must maintain login streak for this many consecutive days</p>
                      </div>
                    )}

                    {/* Course Challenge */}
                    {formData.challenge_type === 'course' && (
                      <div>
                        <Label htmlFor="progress_percentage" className="text-sm font-semibold">Progress Percentage Required *</Label>
                        <Input
                          id="progress_percentage"
                          type="number"
                          min="1"
                          max="100"
                          value={formData.requirements.progress_percentage || 100}
                          onChange={(e) => handleRequirementChange('progress_percentage', e.target.value)}
                          placeholder="100"
                          className="mt-1"
                        />
                        <p className="text-xs text-[#4D4D4D] mt-1">Student must complete this percentage of any course (1-100%)</p>
                      </div>
                    )}

                    {/* Quiz Challenge */}
                    {formData.challenge_type === 'quiz' && (
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="quiz_count" className="text-sm font-semibold">Number of Quizzes *</Label>
                          <Input
                            id="quiz_count"
                            type="number"
                            min="1"
                            value={formData.requirements.quiz_count || 3}
                            onChange={(e) => handleRequirementChange('quiz_count', e.target.value)}
                            placeholder="3"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="min_score" className="text-sm font-semibold">Minimum Score (%) *</Label>
                          <Input
                            id="min_score"
                            type="number"
                            min="1"
                            max="100"
                            value={formData.requirements.min_score || 80}
                            onChange={(e) => handleRequirementChange('min_score', e.target.value)}
                            placeholder="80"
                            className="mt-1"
                          />
                        </div>
                        <p className="text-xs text-[#4D4D4D]">Student must pass this many quizzes with minimum score</p>
                      </div>
                    )}

                    {/* Assignment Challenge */}
                    {formData.challenge_type === 'assignment' && (
                      <div>
                        <Label htmlFor="assignment_count" className="text-sm font-semibold">Number of Assignments *</Label>
                        <Input
                          id="assignment_count"
                          type="number"
                          min="1"
                          value={formData.requirements.assignment_count || 3}
                          onChange={(e) => handleRequirementChange('assignment_count', e.target.value)}
                          placeholder="3"
                          className="mt-1"
                        />
                        <p className="text-xs text-[#4D4D4D] mt-1">Student must submit this many assignments</p>
                      </div>
                    )}

                    {/* Community Challenge */}
                    {formData.challenge_type === 'community' && (
                      <div>
                        <Label htmlFor="post_count" className="text-sm font-semibold">Number of Posts *</Label>
                        <Input
                          id="post_count"
                          type="number"
                          min="1"
                          value={formData.requirements.post_count || 5}
                          onChange={(e) => handleRequirementChange('post_count', e.target.value)}
                          placeholder="5"
                          className="mt-1"
                        />
                        <p className="text-xs text-[#4D4D4D] mt-1">Student must create this many community posts</p>
                      </div>
                    )}

                    {/* Profile Challenge */}
                    {formData.challenge_type === 'profile' && (
                      <div className="text-sm text-[#4D4D4D]">
                        <p>✅ Automatically checks if student's profile is 100% complete</p>
                        <p className="mt-1">Required fields: Name, Email, Phone, Date of Birth, Grade, School Type, Profile Photo</p>
                      </div>
                    )}

                    {/* Manual Completion Challenges */}
                    {['project', 'behaviour', 'special'].includes(formData.challenge_type) && (
                      <div className="text-sm text-[#4D4D4D]">
                        <p>⚠️ This challenge type requires manual completion by admin/teacher</p>
                        <p className="mt-1">Students cannot auto-complete this challenge - you must mark it as complete manually</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Deadline */}
                <div>
                  <Label htmlFor="deadline" className="text-sm font-semibold">Deadline (Optional)</Label>
                  <Input
                    id="deadline"
                    type="datetime-local"
                    value={formData.deadline}
                    onChange={(e) => handleInputChange('deadline', e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* Target Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold">Target Courses (Optional)</Label>
                    <Select 
                      value={formData.target_courses.length > 0 ? formData.target_courses[0] : undefined} 
                      onValueChange={(value) => handleInputChange('target_courses', value ? [value] : [])}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="All courses (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map(course => (
                          <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2 pt-6">
                    <Switch
                      checked={formData.auto_assign_new_students}
                      onCheckedChange={(checked) => handleInputChange('auto_assign_new_students', checked)}
                    />
                    <Label className="text-sm">Auto-assign to new students</Label>
                  </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex space-x-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-[#4CAF50] hover:bg-[#419544] text-white font-semibold py-3 rounded-xl transition-smooth"
                  >
                    {editingChallenge ? 'Update Challenge' : 'Create Challenge'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateModal(false)}
                    className="px-6"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Challenge Cards */}
        {challenges.length === 0 ? (
          <Card className="border-0 card-shadow bg-white rounded-2xl">
            <CardContent className="p-16 text-center">
              <Trophy className="w-20 h-20 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
              <h3 className="text-xl font-bold text-navy mb-2">No Challenges Created</h3>
              <p className="text-[#4D4D4D] font-light">Create your first challenge to gamify student learning!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {challenges.map((challenge) => {
              const typeInfo = getChallengeTypeInfo(challenge.challenge_type);
              const IconComponent = typeInfo.icon;
              const completionRate = challenge.total_assigned > 0 ? 
                Math.round((challenge.total_completed / challenge.total_assigned) * 100) : 0;

              return (
                <Card 
                  key={challenge.id} 
                  className={`border-0 card-shadow card-hover transition-smooth bg-white rounded-2xl overflow-hidden ${
                    !challenge.is_active ? 'opacity-75' : ''
                  }`}
                >
                  {/* Card Header with Type Badge */}
                  <div className={`${typeInfo.color} p-4 text-white relative`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <IconComponent className="w-5 h-5" strokeWidth={2} />
                        <span className="text-sm font-medium">{typeInfo.label}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Award className="w-4 h-4" strokeWidth={2} />
                        <span className="font-bold">{challenge.gp_reward} GP</span>
                      </div>
                    </div>
                    {!challenge.is_active && (
                      <Badge variant="secondary" className="absolute top-2 right-2 bg-red-100 text-red-800">
                        Inactive
                      </Badge>
                    )}
                  </div>

                  <CardContent className="p-5 space-y-4">
                    <h3 className="text-lg font-bold text-navy line-clamp-2">{challenge.title}</h3>
                    <p className="text-sm text-[#4D4D4D] line-clamp-3">{challenge.description}</p>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="text-center p-2 bg-blue-50 rounded-lg">
                        <div className="font-bold text-primary-blue">{challenge.total_assigned}</div>
                        <div className="text-xs text-[#4D4D4D]">Assigned</div>
                      </div>
                      <div className="text-center p-2 bg-green-50 rounded-lg">
                        <div className="font-bold text-green-600">{challenge.total_completed}</div>
                        <div className="text-xs text-[#4D4D4D]">Completed</div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-[#4D4D4D]">Completion Rate</span>
                        <span className="text-xs font-bold text-primary-blue">{completionRate}%</span>
                      </div>
                      <div className="w-full bg-[#E7E7E7] rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-smooth" 
                          style={{ width: `${completionRate}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Deadline */}
                    {challenge.deadline && (
                      <div className="flex items-center space-x-2 text-xs text-[#4D4D4D]">
                        <Calendar className="w-3 h-3" />
                        <span>Due: {formatDate(challenge.deadline)}</span>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => viewCompletions(challenge)}
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                      >
                        <Users className="w-3 h-3 mr-1" />
                        View Progress
                      </Button>
                      <Button
                        onClick={() => handleEdit(challenge)}
                        size="sm"
                        variant="outline"
                        className="text-xs"
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button
                        onClick={() => toggleChallengeStatus(challenge.id, challenge.is_active)}
                        size="sm"
                        variant="outline"
                        className="text-xs"
                      >
                        {challenge.is_active ? 
                          <Clock className="w-3 h-3" /> : 
                          <CheckCircle className="w-3 h-3" />
                        }
                      </Button>
                      <Button
                        onClick={() => handleDelete(challenge.id)}
                        size="sm"
                        variant="outline"
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Challenge Completions Modal */}
        {selectedChallengeCompletions && (
          <Dialog open={true} onOpenChange={() => setSelectedChallengeCompletions(null)}>
            <DialogContent className="max-w-4xl bg-white rounded-2xl border-0 shadow-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-navy">
                  Challenge Progress: {selectedChallengeCompletions.challenge.title}
                </DialogTitle>
              </DialogHeader>

              <div className="max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Assigned</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>GP Earned</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedChallengeCompletions.completions.map(({ completion, student }) => (
                      <TableRow key={completion.id}>
                        <TableCell className="font-medium">
                          {student ? `${student.first_name} ${student.last_name}` : 'Unknown Student'}
                        </TableCell>
                        <TableCell>{student?.email || 'N/A'}</TableCell>
                        <TableCell>
                          <Badge 
                            variant={
                              completion.status === 'completed' ? 'default' : 
                              completion.status === 'in_progress' ? 'secondary' : 'outline'
                            }
                            className={
                              completion.status === 'completed' ? 'bg-green-100 text-green-800' :
                              completion.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }
                          >
                            {completion.status.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(completion.assigned_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {completion.completed_at ? new Date(completion.completed_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          {completion.gp_awarded > 0 ? (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              {completion.gp_awarded} GP
                            </Badge>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}